import { format, getTime, formatDistanceToNow, addDays, subDays, addMonths } from 'date-fns';
import moment from 'moment';

// ----------------------------------------------------------------------

export const DEFAULT_TIME_FORMAT = 'hh:mm aaa';
export const DEFAULT_DATE_FORMAT = 'dd MMM yyyy';
export const DEFAULT_DATE_TIME_FORMAT = 'dd MMM yyyy p';
export const DEFAULT_DATE_FILTER_FORMAT = 'yyyy-MM-dd';
export const MOMENT_DEFAULT_DATE_FORMAT = 'YYYY-MM-DD';
export const DATE_FORMAT_SLASH = 'dd/MM/yyyy';
export const DEFAULT_DATE_FORMAT_2 = 'DD/MM/YYYY';
export const DATE_FORMAT_NUMERIC = 'MM/dd/yyyy'; // Month/Day/Year numeric
export const DAY_FORMAT_FULL = 'EEEE'; // Full weekday name
export const YEAR_FIRST_DATE_FORMAT = 'YYYY-MM-DD';
export const TIME_FORMAT_12H = 'hh:mm a';
export const TIME_FORMAT_12H_CAPITAL = 'hh:mm A';
export const TIME_INPUT_FORMAT = 'HH:mm:ss';
/** API / persisted strings like `"02:00 pm"` — must be tried after `HH:mm:ss` because lenient 24h parse ignores meridiem. */
export const TIME_STRING_12H_INPUT_FORMAT = 'hh:mm a';
export const DATE_FORMAT_SLASH_WITH_SHORT_YEAR = 'dd/MM/yy';
export const DATE_FORMAT_MMM_DD_SHORT = 'MMM dd';

export function fDate(date: string | Date, newFormat?: string) {
  const fm = newFormat || DEFAULT_DATE_FORMAT;

  return date ? format(date instanceof Date ? date : new Date(date), fm) : '';
}

export function fDateTime(date:string, newFormat: string) {
  const fm = newFormat || DEFAULT_DATE_TIME_FORMAT;

  return date ? format(new Date(date), fm) : '';
}

export function fTimestamp(date:string) {
  return date ? getTime(new Date(date)) : '';
}

export function fToNow(date:string) {
  return date
    ? formatDistanceToNow(new Date(date), {
        addSuffix: true,
      })
    : '';
}

export function timeDiffFromNow(timestamp: string): string {
  const now = moment();
  const target = moment(timestamp);
  const duration = moment.duration(target.diff(now));

  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();

  return `${hours > 0 ? `${hours}h ` : ''}${minutes}m`;
}

export function addDaysToDate(days: number, date: Date = new Date(), format: string = DEFAULT_DATE_FILTER_FORMAT) {
  return fDate(addDays(date, days), format);
}

export function addMonthsToDate(months: number, date: Date = new Date(), format: string = DEFAULT_DATE_FILTER_FORMAT) {
  return fDate(addMonths(date, months), format);
}

export const subtractDaysFromDate = (
  days: number,
  date: string = new Date().toISOString(),
  format: string = DEFAULT_DATE_FILTER_FORMAT
) => {
  return fDate(subDays(new Date(date), days), format);
};

export const getRelativeTime = (dateString: string) => {
  return moment(dateString).fromNow();
};

// Utility function to format timestamp for display
export const formatMessageTimestamp = (isoTimestamp: string): string => {
  const date = new Date(isoTimestamp);
  const now = new Date();

  // Get start of today
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  // Calculate difference in days
  const diffInMs = today.getTime() - messageDate.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) {
    // Message sent today → show hh:mm AM/PM format
    return format(date, TIME_FORMAT_12H); // "11:42 AM"
  } else if (diffInDays === 1) {
    // Message was yesterday → show Yesterday
    return 'Yesterday';
  } else if (diffInDays >= 2 && diffInDays <= 6) {
    // For messages 2–6 days ago → show the day name
    return format(date, DAY_FORMAT_FULL); // "Sunday", "Saturday"
  } else {
    // If message is 6+ days old → show full date (DD/MM/YYYY)
    return format(date, 'dd/MM/yyyy'); // "25/12/2024"
  }
};

export function transformDate(inputDate: string, outputFormat = YEAR_FIRST_DATE_FORMAT) {
  const parsedDate = moment(new Date(inputDate));
  if (!parsedDate.isValid()) {
    return '';
  }
  return parsedDate.format(outputFormat);
}

// Converts a time string (e.g., "02:30 PM") to a Date object
export const parseTimeStringToDate = (timeString: string, format: string = TIME_FORMAT_12H) => {
  return moment(timeString, format).toDate();
};

// Formats a Date object into a time string (e.g., "02:30 PM")
export const formatDateToTimeString = (time: Date, format: string = TIME_FORMAT_12H) => {
  return moment(time).format(format);
};

// Converts a time string (e.g., "02:30 PM") to a Date object
export const formatWithMoment = (timeString: string, format: string = TIME_FORMAT_12H) => {
  return moment(timeString, format);
};

// Formats a String date into a time string (e.g., "02:30 PM")
export const formatStringToTimeString = (
  time: string,
  inputFormat: string = TIME_INPUT_FORMAT,
  outputFormat: string = TIME_FORMAT_12H
) => {
  if (!time) return '';
  // Strict parse first: `moment("02:00 pm", "HH:mm:ss")` is lenient and treats 02 as 24h hour 2,
  // ignoring "pm" — so we must try 12h patterns when the preferred input format does not match.
  const candidates = [
    inputFormat,
    TIME_STRING_12H_INPUT_FORMAT,
    TIME_FORMAT_12H,
    TIME_FORMAT_12H_CAPITAL,
  ];
  for (const fmt of [...new Set(candidates)]) {
    const parsed = moment(time, fmt, true);
    if (parsed.isValid()) return parsed.format(outputFormat);
  }
  return moment(time, inputFormat).format(outputFormat);
};

const isTimeText = (value?: string | null): value is string => !!value && /\d{1,2}:\d{2}/.test(value);

export const formatTimeRange = (startTime?: string | null, endTime?: string | null, separator = '-') => {
  const parts = [startTime, endTime].filter(isTimeText).map((value) => value.trim());
  return parts.length ? parts.join(separator) : '—';
};
